// LearnCore Frontend - 图片输入组件

import React, { useState, useRef } from 'react';
import { apiClient } from '../api/client';
import { InlineLoading } from './Loading';
import { 
  PhotoIcon, 
  XMarkIcon,
  LinkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

interface ImageInputProps {
  label: string;
  imageUrl?: string;
  onImageChange: (imageUrl: string | undefined) => void;
  disabled?: boolean;
}

interface ImagePreviewProps {
  imageUrl: string;
  onRemove: () => void;
  disabled?: boolean;
}

// 图片预览组件
const ImagePreview: React.FC<ImagePreviewProps> = ({ imageUrl, onRemove, disabled }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  // 处理图片加载错误
  const handleImageError = () => {
    setImageError(true);
  };

  // 如果是markdown格式的图片链接，解析出真实URL
  const getRealImageUrl = (url: string): string => {
    // 匹配 ![alt](url) 格式
    const markdownMatch = url.match(/!\[.*?\]\((.*?)\)/);
    if (markdownMatch) {
      return markdownMatch[1];
    }
    return url;
  };

  const realImageUrl = getRealImageUrl(imageUrl);

  if (imageError) {
    return (
      <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
        <div className="text-gray-500 text-sm">
          <PhotoIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          图片加载失败
          <div className="text-xs text-gray-400 mt-1 break-all">
            {imageUrl}
          </div>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-sm border border-gray-200 hover:bg-gray-50"
          >
            <XMarkIcon className="h-4 w-4 text-gray-600" />
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="relative">
        <img
          src={realImageUrl}
          alt="上传的图片"
          className="w-full h-32 object-cover rounded-lg border border-gray-200"
          onError={handleImageError}
        />
        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center opacity-0 hover:opacity-100">
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            className="p-2 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow"
          >
            <EyeIcon className="h-4 w-4 text-gray-600" />
          </button>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-sm border border-gray-200 hover:bg-gray-50"
          >
            <XMarkIcon className="h-4 w-4 text-gray-600" />
          </button>
        )}
        {imageUrl.startsWith('![') && (
          <div className="absolute bottom-2 left-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
            Markdown
          </div>
        )}
      </div>

      {/* 图片预览模态窗口 */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={() => setPreviewOpen(false)}>
          <div className="max-w-4xl max-h-[90vh] p-4">
            <img
              src={realImageUrl}
              alt="图片预览"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <button
            onClick={() => setPreviewOpen(false)}
            className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100"
          >
            <XMarkIcon className="h-6 w-6 text-gray-600" />
          </button>
        </div>
      )}
    </>
  );
};

export const ImageInput: React.FC<ImageInputProps> = ({ 
  label, 
  imageUrl, 
  onImageChange, 
  disabled = false 
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 处理文件上传
  const handleFileUpload = async (file: File) => {
    try {
      setUploading(true);
      setError(null);
      
      const result = await apiClient.uploadImage(file);
      onImageChange(result.image_url);
      
    } catch (err: any) {
      setError(err.response?.data?.error || '图片上传失败');
    } finally {
      setUploading(false);
    }
  };

  // 处理文件选择
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // 处理URL输入
  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onImageChange(urlInput.trim());
      setUrlInput('');
      setShowUrlInput(false);
    }
  };

  // 处理Markdown格式输入
  const handleMarkdownSubmit = () => {
    if (urlInput.trim()) {
      // 检查是否已经是markdown格式
      if (urlInput.includes('![') && urlInput.includes('](')) {
        onImageChange(urlInput.trim());
      } else {
        // 转换为markdown格式
        onImageChange(`![图片](${urlInput.trim()})`);
      }
      setUrlInput('');
      setShowUrlInput(false);
    }
  };

  // 移除图片
  const handleRemoveImage = () => {
    onImageChange(undefined);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>

      {/* 显示现有图片 */}
      {imageUrl && (
        <ImagePreview 
          imageUrl={imageUrl} 
          onRemove={handleRemoveImage}
          disabled={disabled}
        />
      )}

      {/* 上传控件 */}
      {!imageUrl && !disabled && (
        <div className="space-y-3">
          {/* 文件上传区域 */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
            <PhotoIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <div className="text-sm text-gray-600 mb-3">
              拖放图片到此处，或点击选择文件
            </div>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="btn-outline text-sm"
              >
                {uploading ? (
                  <div className="flex items-center space-x-2">
                    <InlineLoading size="sm" />
                    <span>上传中...</span>
                  </div>
                ) : (
                  <>
                    <PhotoIcon className="h-4 w-4 mr-1" />
                    选择文件
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowUrlInput(true)}
                disabled={uploading}
                className="btn-outline text-sm ml-2"
              >
                <LinkIcon className="h-4 w-4 mr-1" />
                使用链接
              </button>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              支持 JPEG、PNG、GIF、WebP 格式，最大 10MB
            </div>
          </div>

          {/* 隐藏的文件输入 */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
        </div>
      )}

      {/* URL输入模态 */}
      {showUrlInput && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-medium text-gray-900">添加图片链接</h4>
            <button
              type="button"
              onClick={() => {
                setShowUrlInput(false);
                setUrlInput('');
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
          
          <div>
            <input
              type="url"
              placeholder="输入图片URL或Markdown格式..."
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              className="input w-full text-sm"
            />
            <div className="text-xs text-gray-500 mt-1">
              支持直接URL或Markdown格式：![描述](图片URL)
            </div>
          </div>
          
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={handleUrlSubmit}
              disabled={!urlInput.trim()}
              className="btn-primary text-sm flex-1"
            >
              添加URL
            </button>
            <button
              type="button"
              onClick={handleMarkdownSubmit}
              disabled={!urlInput.trim()}
              className="btn-outline text-sm flex-1"
            >
              Markdown格式
            </button>
          </div>
        </div>
      )}

      {/* 错误提示 */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </div>
      )}
    </div>
  );
};

export default ImageInput;

